import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionResetIconUrl from '@/assets/action-menu/reset-password.svg'
import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Input } from '@/components/forms/input'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'

import {
  createOAuthResource,
  deleteOAuthResource,
  getOidcProviderConfiguration,
  listOidcClientScopes,
  listOAuthResources,
  rotateOAuthResourceSecret,
  updateOAuthResource,
  type OAuthResource,
  type OAuthResourceCreated,
  type OAuthResourcePayload,
} from './api'
import { OAuthResourceCredentialsModal } from './idp-oauth-resource-credentials-modal'
import { OAuthResourceFormDrawer } from './idp-oauth-resource-form-drawer'
import { isInsecureHttpResourceUri } from './idp-oauth-resource-uri'
import { IdpConfigurationFlow } from './idp-configuration-flow'
import { isOidcIdpReadOnly } from './idp-permissions'

const queryKey = ['admin-idp-oauth-resources']

export function AdminOAuthResourcesPage() {
  const { t } = useTranslation('adminIdp')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editingResource, setEditingResource] = useState<OAuthResource | null>(null)
  const [credentials, setCredentials] = useState<OAuthResourceCreated | null>(null)
  const [pendingAction, setPendingAction] = useState<{
    resource: OAuthResource
    type: 'delete' | 'rotate'
  } | null>(null)
  const resourcesQuery = useQuery({ queryFn: listOAuthResources, queryKey })
  const scopesQuery = useQuery({
    queryFn: listOidcClientScopes,
    queryKey: ['admin-idp-client-scopes'],
  })
  const providerQuery = useQuery({
    queryFn: getOidcProviderConfiguration,
    queryKey: ['admin-idp-provider'],
  })
  const invalidate = () => void queryClient.invalidateQueries({ queryKey })
  const createMutation = useMutation({
    mutationFn: createOAuthResource,
    onError: () => showFeedback({ message: t('resources.errors.create'), tone: 'error' }),
    onSuccess: (resource) => {
      invalidate()
      setCreateOpen(false)
      setCredentials(resource)
      showFeedback({ message: t('resources.success.created'), tone: 'success' })
    },
  })
  const updateMutation = useMutation({
    mutationFn: updateOAuthResource,
    onError: () => showFeedback({ message: t('resources.errors.update'), tone: 'error' }),
    onSuccess: () => {
      invalidate()
      setEditingResource(null)
      showFeedback({ message: t('resources.success.updated'), tone: 'success' })
    },
  })
  const rotateMutation = useMutation({
    mutationFn: rotateOAuthResourceSecret,
    onError: () => showFeedback({ message: t('resources.errors.rotateSecret'), tone: 'error' }),
    onSuccess: (resource) => {
      invalidate()
      setPendingAction(null)
      setCredentials(resource)
      showFeedback({ message: t('resources.success.secretRotated'), tone: 'success' })
    },
  })
  const deleteMutation = useMutation({
    mutationFn: deleteOAuthResource,
    onError: () => showFeedback({ message: t('resources.errors.delete'), tone: 'error' }),
    onSuccess: () => {
      invalidate()
      setPendingAction(null)
      showFeedback({ message: t('resources.success.deleted'), tone: 'success' })
    },
  })

  const rows = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase()
    if (!normalized) return resourcesQuery.data ?? []
    return (resourcesQuery.data ?? []).filter((resource) =>
      [resource.name, resource.resource_uri, resource.introspection_client_id]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalized),
    )
  }, [resourcesQuery.data, search])

  const columns = useMemo<DataTableColumn<OAuthResource>[]>(
    () => [
      {
        header: t('resources.columns.name'),
        id: 'name',
        render: (resource) => <span className="font-medium">{resource.name}</span>,
        width: 220,
      },
      {
        header: t('resources.columns.resourceUri'),
        id: 'resourceUri',
        render: (resource) => (
          <div className="flex items-center gap-2">
            <code className="text-xs text-[var(--color-text-muted)]">{resource.resource_uri}</code>
            {isInsecureHttpResourceUri(resource.resource_uri) ? (
              <Badge variant="warning">{t('resources.insecureHttpBadge')}</Badge>
            ) : null}
          </div>
        ),
        width: 360,
      },
      {
        header: t('resources.columns.introspectionClientId'),
        id: 'introspectionClientId',
        render: (resource) => (
          <code className="text-xs text-[var(--color-text-muted)]">
            {resource.introspection_client_id}
          </code>
        ),
        width: 280,
      },
      {
        header: t('resources.columns.status'),
        id: 'status',
        render: (resource) => (
          <Badge variant={resource.status === 'active' ? 'success' : 'neutral'}>
            {resource.status === 'active' ? t('status.enabled') : t('status.disabled')}
          </Badge>
        ),
        width: 100,
      },
      {
        align: 'right',
        header: t('resources.columns.actions'),
        id: 'actions',
        pin: 'right',
        render: (resource) =>
          readOnly ? null : (
            <ActionMenu
              ariaLabel={t('resources.rowActions.open', { name: resource.name })}
              items={[
                {
                  iconSrc: actionEditIconUrl,
                  key: 'edit',
                  label: t('actions.edit'),
                  onSelect: () => setEditingResource(resource),
                },
                {
                  disabled: rotateMutation.isPending,
                  iconSrc: actionResetIconUrl,
                  key: 'rotate',
                  label: t('resources.rowActions.rotateSecret'),
                  onSelect: () => setPendingAction({ resource, type: 'rotate' }),
                },
                {
                  disabled: deleteMutation.isPending,
                  iconSrc: actionDeleteIconUrl,
                  key: 'delete',
                  label: t('actions.delete'),
                  onSelect: () => setPendingAction({ resource, type: 'delete' }),
                  variant: 'danger' as const,
                },
              ]}
            />
          ),
        width: 90,
      },
    ],
    [deleteMutation, readOnly, rotateMutation, t],
  )

  const saveEdit = (payload: OAuthResourcePayload) => {
    if (editingResource) updateMutation.mutate({ payload, resourceId: editingResource.id })
  }

  return (
    <AdminShellPage activePath="/admin/idp/resources">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          actions={
            readOnly ? null : (
              <Button onClick={() => setCreateOpen(true)}>{t('resources.create')}</Button>
            )
          }
          description={t('resources.description')}
          title={t('resources.title')}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-[30px]">
          <div className="mb-5">
            <IdpConfigurationFlow active="resources" />
          </div>
          <div className="mb-4 max-w-[420px]">
            <Input
              aria-label={t('resources.search')}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('resources.search')}
              value={search}
            />
          </div>
          {resourcesQuery.isError ? (
            <p className="text-sm text-[var(--color-danger)]">{t('resources.errors.load')}</p>
          ) : (
            <DataTable
              columns={columns}
              empty={
                <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
                  {t('resources.empty')}
                </div>
              }
              getRowKey={(resource) => resource.id}
              isLoading={resourcesQuery.isLoading}
              minWidth={1120}
              rows={rows}
            />
          )}
        </div>
      </div>

      {readOnly ? null : (
        <OAuthResourceFormDrawer
          isSaving={createMutation.isPending}
          key={createOpen ? 'create-open' : 'create-closed'}
          onOpenChange={setCreateOpen}
          onSave={(payload) => createMutation.mutate(payload)}
          open={createOpen}
          scopes={scopesQuery.data ?? []}
        />
      )}
      {editingResource ? (
        <OAuthResourceFormDrawer
          isSaving={updateMutation.isPending}
          key={editingResource.id}
          onOpenChange={(open) => {
            if (!open) setEditingResource(null)
          }}
          onSave={saveEdit}
          open
          resource={editingResource}
          scopes={scopesQuery.data ?? []}
        />
      ) : null}
      <OAuthResourceCredentialsModal
        key={
          credentials
            ? `${credentials.id}:${credentials.updated_at ?? credentials.created_at ?? 'current'}`
            : 'closed'
        }
        onClose={() => setCredentials(null)}
        onRetryProvider={() => void providerQuery.refetch()}
        provider={providerQuery.data ?? null}
        providerError={providerQuery.isError}
        providerLoading={providerQuery.isFetching}
        resource={credentials}
      />
      <ConfirmationDialog
        cancelLabel={t('actions.cancel')}
        confirmLabel={
          pendingAction?.type === 'delete' ? t('actions.delete') : t('actions.rotateSecret')
        }
        description={
          pendingAction?.type === 'delete'
            ? t('resources.confirmDelete', { name: pendingAction.resource.name })
            : t('resources.confirmRotate', { name: pendingAction?.resource.name ?? '' })
        }
        isConfirming={deleteMutation.isPending || rotateMutation.isPending}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => {
          if (!pendingAction) return
          if (pendingAction.type === 'delete') deleteMutation.mutate(pendingAction.resource.id)
          else rotateMutation.mutate(pendingAction.resource.id)
        }}
        open={Boolean(pendingAction)}
        title={
          pendingAction?.type === 'delete'
            ? t('resources.confirmDeleteTitle')
            : t('resources.confirmRotateTitle')
        }
        tone={pendingAction?.type === 'delete' ? 'danger' : 'warning'}
      />
    </AdminShellPage>
  )
}
